import { useState } from 'react'
import { css } from '@emotion/react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { aliveInvestments } from '../investmentUtils'
import { SortArrow } from './InstitutionalInvestmentsGroupStyle'
import { RowList, RowHeader, Type, Ticker, Name, Balance, HeaderLabel } from './InvestmentRowStyle'
import InvestmentGroupItem from './InvestmentGroupItem'

const SORT_VALUE = {
  balance: investment => investment.value,
  name: investment => investment.securities.securityName.toLowerCase(),
}

const sortableHeader = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
`

const sortableBalanceHeader = css`
  ${sortableHeader};
  justify-content: flex-end;
`

const arrowDirection = (sort, column) => {
  if (sort.by !== column) return 'neutral'
  return sort.order === 'asc' ? 'up' : 'down'
}

/**
 * Renders sortable column headers and the investment rows for a single group.
 *
 * @param {object} props
 * @param {Array} props.investments Investments to sort and render.
 * @param {boolean} [props.hideBalance] Hides the balance column and initially sorts by name.
 */
const InvestmentRows = ({ accountId, canEdit, hideBalance, investments, toggleEdit }) => {
  const [sort, setSort] = useState(
    hideBalance ? { by: 'name', order: 'asc' } : { by: 'balance', order: 'desc' }
  )

  const handleSortChange = column =>
    setSort(current =>
      column === current.by
        ? { ...current, order: current.order === 'asc' ? 'desc' : 'asc' }
        : { by: column, order: 'desc' }
    )

  // Sorting by balance reads each investment's `value` view, so dead nodes have to go before the
  // sort rather than at render time.
  const sortedInvestments = _.orderBy(
    aliveInvestments(investments),
    SORT_VALUE[sort.by],
    sort.order
  )

  return (
    <>
      {/* Keep the header and rows as siblings in the parent grid so their columns stay aligned. */}
      <RowHeader>
        <Type>Investment type</Type>
        <Ticker>Ticker</Ticker>
        <Name css={sortableHeader} onClick={() => handleSortChange('name')}>
          <HeaderLabel>Investment name</HeaderLabel>
          <SortArrow direction={arrowDirection(sort, 'name')} />
        </Name>
        {!hideBalance && (
          <Balance css={sortableBalanceHeader} onClick={() => handleSortChange('balance')}>
            Balance
            <SortArrow direction={arrowDirection(sort, 'balance')} />
          </Balance>
        )}
      </RowHeader>

      <RowList>
        {sortedInvestments.map(investment => (
          <InvestmentGroupItem
            key={investment.id}
            accountId={accountId}
            canEdit={canEdit}
            investment={investment}
            toggleEdit={toggleEdit}
          />
        ))}
      </RowList>
    </>
  )
}

InvestmentRows.propTypes = {
  investments: PropTypes.array.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canEdit: PropTypes.bool,
  toggleEdit: PropTypes.func,
  hideBalance: PropTypes.bool,
}

export default observer(InvestmentRows)
